/**
 * Capacidade por dia com vagas por evento (regra pura, usada pela função check-supplier-capacity e pela loja/painel).
 *
 * Cada evento ocupa 1 vaga, a menos que uma regra do fornecedor diga outro número:
 *  - por tipo de evento (ex.: Casamento ocupa o dia todo);
 *  - por quantidade de convidados (ex.: acima de 100 convidados ocupa o dia todo).
 * "O dia todo" = a capacidade do dia. Se o evento cai em mais de uma regra, vale a que ocupa mais vagas.
 * O dia está livre para um pedido novo quando: vagas já ocupadas + vagas do pedido novo <= capacidade do dia.
 */

export type CapacitySlots = 'all' | number;

export interface CapacityTypeRule { type: string; slots: CapacitySlots }
export interface CapacityGuestsRule { above: number; slots: CapacitySlots }

export interface CapacityRules {
  enabled?: boolean;
  by_type?: CapacityTypeRule[];
  by_guests?: CapacityGuestsRule[];
}

export interface CapacityRequest {
  eventType?: string | null;
  guests?: number | string | null;
}

const norm = (value: unknown): string =>
  String(value ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();

const toGuests = (value: unknown): number => {
  const n = parseInt(String(value ?? ''), 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/** Vagas de uma regra: "o dia todo" vale a capacidade; número é limitado a 1..capacidade. Sem capacidade conhecida, vale 1. */
const resolveSlots = (slots: CapacitySlots, capacity: number | null): number => {
  if (capacity === null || !(capacity >= 1)) return 1;
  if (slots === 'all') return capacity;
  const n = Math.floor(Number(slots));
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.min(n, capacity);
};

/** Quantas vagas do dia o evento ocupa. */
export const slotsForEvent = (event: CapacityRequest, rules: CapacityRules | null | undefined, capacity: number | null): number => {
  if (!rules?.enabled) return 1;
  let slots = 1;
  const type = norm(event.eventType);
  if (type) {
    for (const rule of rules.by_type ?? []) {
      if (norm(rule.type) === type) slots = Math.max(slots, resolveSlots(rule.slots, capacity));
    }
  }
  const guests = toGuests(event.guests);
  if (guests > 0) {
    for (const rule of rules.by_guests ?? []) {
      if (Number(rule.above) >= 0 && guests > Number(rule.above)) slots = Math.max(slots, resolveSlots(rule.slots, capacity));
    }
  }
  return slots;
};

/** O dia comporta o pedido novo? `used` = vagas já ocupadas no dia; capacidade nula = sem limite. */
export const dayFitsRequest = (used: number, capacity: number | null, requestSlots: number): boolean =>
  capacity === null || used + requestSlots <= capacity;
